import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { submitAnalysis, getDenialAnalysis } from '../../../utils/denialAnalysisApi';

const STATUS_MESSAGES = {
  analyzing: 'Analyzing denial letter with AI...',
  ocr_complete: 'Ready to analyze.',
  ocr_failed: 'OCR extraction failed. Please re-upload the document.',
  failed: 'Analysis failed. Please try again.',
  complete: 'Analysis complete!',
};

const AnalyzeStep = ({ analysis, onComplete, onUpdate }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const response = await getDenialAnalysis(analysis.id);
        const updated = response.data;
        onUpdate(updated);

        if (updated.status === 'complete') {
          stopPolling();
          onComplete(updated);
        } else if (updated.status === 'failed') {
          stopPolling();
          setError('Analysis failed. Please try again.');
          setSubmitting(false);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
  };

  useEffect(() => {
    // If already analyzing, start polling immediately
    if (analysis?.status === 'analyzing') {
      setSubmitting(true);
      startPolling();
    }
    return () => stopPolling();
  }, [analysis?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnalyze = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await submitAnalysis(analysis.id);
      startPolling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit analysis');
      setSubmitting(false);
    }
  };

  const currentStatus = analysis?.status;
  const statusMessage = STATUS_MESSAGES[currentStatus] || '';
  const isAnalyzing = currentStatus === 'analyzing' || submitting;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Analyze Denial Letter
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Our AI will classify the denial, extract codes, explain the reason in plain English,
        and generate prioritized next steps.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {currentStatus === 'ocr_failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          OCR extraction failed. Please go back and re-upload the document.
        </Alert>
      )}

      {isAnalyzing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={24} />
          <Typography>{statusMessage || 'Analyzing...'}</Typography>
        </Box>
      )}

      {!isAnalyzing && currentStatus !== 'complete' && (
        <Button
          variant="contained"
          size="large"
          onClick={handleAnalyze}
          disabled={currentStatus === 'ocr_failed'}
          fullWidth
        >
          Analyze Denial Letter
        </Button>
      )}
    </Box>
  );
};

export default AnalyzeStep;
